import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CalendarDays,
  Download,
  Mail,
  MessageCircleQuestion,
  Trash2,
  UsersRound,
} from "lucide-react";
import { toast } from "sonner";
import { Container } from "@/components/Container";
import { SectionHeading } from "@/components/SectionHeading";
import AdminGate from "@/components/admin/AdminGate";
import {
  deleteLiveReviewInterest,
  downloadCsv,
  getLiveReviewInterest,
  updateLiveReviewInterestStatus,
} from "@/lib/api";

const STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "invited", label: "Invited" },
  { value: "booked", label: "Booked" },
  { value: "not_interested", label: "Not interested" },
];

const HELP_LABELS = {
  group: "Group workshop",
  private: "Private tutoring",
  both: "Both options",
};

const TIME_LABELS = {
  weekday_evening: "Weekday evening",
  saturday: "Saturday",
  sunday: "Sunday",
  flexible: "Flexible",
};

function formatDate(value, includeTime = false) {
  if (!value) return "Not scheduled";
  try {
    const date = value.length === 10 ? new Date(`${value}T12:00:00`) : new Date(value);
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      ...(includeTime ? { hour: "numeric", minute: "2-digit" } : {}),
    });
  } catch (error) {
    return value;
  }
}

const Summary = ({ label, value }) => (
  <div className="border-l-4 border-l-[var(--ibp-gold-500)] bg-white px-5 py-4">
    <p className="text-2xl font-bold text-[var(--ibp-ink)]">{value}</p>
    <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-[color:rgba(11,18,32,0.55)]">
      {label}
    </p>
  </div>
);

function InterestManager() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin-live-review-interest"],
    queryFn: getLiveReviewInterest,
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }) => updateLiveReviewInterestStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-live-review-interest"] });
      toast.success("Interest status updated");
    },
    onError: () => toast.error("Could not update status"),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteLiveReviewInterest,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-live-review-interest"] });
      toast.success("Interest record deleted");
    },
    onError: () => toast.error("Could not delete record"),
  });

  const rows = useMemo(() => data || [], [data]);
  const summary = useMemo(
    () => ({
      total: rows.length,
      group: rows.filter((item) => ["group", "both"].includes(item.help_type)).length,
      private: rows.filter((item) => ["private", "both"].includes(item.help_type)).length,
      booked: rows.filter((item) => item.status === "booked").length,
    }),
    [rows]
  );

  if (isLoading) {
    return <p className="py-10 text-center text-sm text-[color:rgba(11,18,32,0.6)]">Loading interest list...</p>;
  }
  if (isError) {
    return <p className="border-l-4 border-l-[#B42318] bg-white px-4 py-3 text-sm text-[#8f2118]">Could not load the priority list. Refresh and try again.</p>;
  }

  return (
    <>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Summary label="Total interest" value={summary.total} />
        <Summary label="Group interest" value={summary.group} />
        <Summary label="Private interest" value={summary.private} />
        <Summary label="Booked" value={summary.booked} />
      </div>

      <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[var(--ibp-ink)]">Priority-list submissions</h2>
          <p className="mt-1 text-sm text-[color:rgba(11,18,32,0.6)]">
            Update each person as you contact, invite, or book them.
          </p>
        </div>
        <button
          type="button"
          onClick={async () => {
            try {
              await downloadCsv("/admin/live-review-interest/export", "live_review_interest.csv");
            } catch (error) {
              toast.error("Could not export the priority list");
            }
          }}
          disabled={!rows.length}
          className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg border border-[color:rgba(10,27,51,0.16)] bg-white px-4 py-2 text-sm font-semibold text-[var(--ibp-ink)] disabled:opacity-50"
        >
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </div>

      {rows.length === 0 ? (
        <div className="mt-6 border border-dashed border-[color:rgba(10,27,51,0.2)] bg-white px-6 py-14 text-center">
          <UsersRound className="mx-auto h-7 w-7 text-[color:rgba(11,18,32,0.38)]" />
          <p className="mt-3 text-base font-semibold text-[var(--ibp-ink)]">No interest submissions yet</p>
          <p className="mt-1 text-sm text-[color:rgba(11,18,32,0.6)]">New priority-list requests will appear here.</p>
        </div>
      ) : (
        <div className="mt-6 space-y-3">
          {rows.map((entry) => (
            <article key={entry.id} className="border border-[color:rgba(10,27,51,0.12)] bg-white p-5">
              <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <h3 className="text-base font-semibold text-[var(--ibp-ink)]">{entry.name}</h3>
                    <span className="text-xs text-[color:rgba(11,18,32,0.48)]">Joined {formatDate(entry.created_at, true)}</span>
                  </div>
                  <a href={`mailto:${entry.email}`} className="mt-1 inline-flex items-center gap-1.5 break-all text-sm font-medium text-[#0F3D8A] hover:underline">
                    <Mail className="h-3.5 w-3.5 flex-none" /> {entry.email}
                  </a>
                  <div className="mt-4 grid gap-x-8 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-[color:rgba(11,18,32,0.48)]">Exam date</p>
                      <p className="mt-1 flex items-center gap-1.5 text-[var(--ibp-ink)]"><CalendarDays className="h-3.5 w-3.5" /> {formatDate(entry.exam_date)}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-[color:rgba(11,18,32,0.48)]">Preferred time</p>
                      <p className="mt-1 text-[var(--ibp-ink)]">{entry.preferred_time_label || TIME_LABELS[entry.preferred_time] || "Flexible"}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-[color:rgba(11,18,32,0.48)]">Interested in</p>
                      <p className="mt-1 text-[var(--ibp-ink)]">{entry.help_type_label || HELP_LABELS[entry.help_type] || entry.help_type}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-[color:rgba(11,18,32,0.48)]">Source</p>
                      <p className="mt-1 capitalize text-[var(--ibp-ink)]">{entry.source || "website"}</p>
                    </div>
                  </div>
                  <p className="mt-4 flex items-start gap-2 text-sm text-[color:rgba(11,18,32,0.72)]">
                    <MessageCircleQuestion className="mt-0.5 h-4 w-4 flex-none text-[#B7791F]" />
                    <span><strong className="font-semibold text-[var(--ibp-ink)]">Hardest topic:</strong> {entry.hardest_topic}</span>
                  </p>
                </div>

                <div className="flex flex-none items-center gap-2">
                  <label className="sr-only" htmlFor={`interest-status-${entry.id}`}>Status for {entry.name}</label>
                  <select
                    id={`interest-status-${entry.id}`}
                    value={entry.status || "new"}
                    onChange={(event) => statusMutation.mutate({ id: entry.id, status: event.target.value })}
                    disabled={statusMutation.isPending}
                    className="h-10 rounded-lg border border-[color:rgba(10,27,51,0.16)] bg-white px-3 text-sm font-semibold text-[var(--ibp-ink)]"
                  >
                    {STATUS_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                  <button
                    type="button"
                    title="Delete interest record"
                    aria-label={`Delete interest record for ${entry.name}`}
                    onClick={() => {
                      if (window.confirm(`Delete the priority-list record for ${entry.name}?`)) {
                        deleteMutation.mutate(entry.id);
                      }
                    }}
                    disabled={deleteMutation.isPending}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-[color:rgba(11,18,32,0.45)] hover:bg-[color:rgba(180,35,24,0.07)] hover:text-[#B42318] disabled:opacity-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </>
  );
}

export default function AdminLiveReview() {
  return (
    <div className="bg-[var(--ibp-offwhite)]">
      <section className="bg-[var(--ibp-navy-950)] py-12 text-white sm:py-16">
        <Container>
          <SectionHeading
            tone="navy"
            kicker="Store admin"
            title="Live review interest"
            subtitle="Manage priority requests for upcoming group reviews and private tutoring availability."
          />
        </Container>
      </section>
      <section className="py-12 sm:py-16">
        <Container>
          <AdminGate>
            <InterestManager />
          </AdminGate>
        </Container>
      </section>
    </div>
  );
}
